import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import 'material-symbols/outlined.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { TopBarActionsProvider } from './contexts/TopBarActionsContext';
import Sidebar from './components/common/Sidebar/Sidebar';
import TopBar from './components/common/TopBar/TopBar';
import AppRoutes from './routes/AppRoutes';
import { getCurrentHostApp, isFeatureHostApp } from './config/hostApps';
import DeprecatedPortalHostRedirect from './components/routing/DeprecatedPortalHostRedirect';
import './App.css';

const MOBILE_BREAKPOINT = 840;
const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const location = useLocation();
  const hostApp = getCurrentHostApp();

  const CHROMELESS_PATHS = [
    '/login',
    '/aplicar',
    '/cadastro',
    '/checkout',
    '/checkout/success',
    '/checkout/awaiting-payment',
    '/quero-comecar',
    '/onboarding',
    '/cinta-natural',
    '/landpage',
    '/fup',
    '/success',
    '/awaiting-payment',
    // Compat paths antigos (redirect)
    '/anamnese',
    '/parq',
    '/par-q',
    '/enviar-fotos',
    '/enviar-equipamentos',
    '/equipamentos',
    '/diario-alimentar',
    '/enviar-exames',
    '/enviar-medidas',
    '/enviar-suplementos',
  ];
  const isChromelessPath = hostApp === 'disabled'
    || isFeatureHostApp(hostApp)
    || CHROMELESS_PATHS.includes(location.pathname)
    || location.pathname.startsWith('/onboarding')
    || location.pathname.startsWith('/checkout/')
    || location.pathname.startsWith('/fup/')
    || location.pathname.startsWith('/diario-alimentar')
    || location.pathname.startsWith('/enviar-exames')
    || location.pathname.startsWith('/enviar-medidas')
    || location.pathname.startsWith('/enviar-suplementos');
  const showChrome = !!user && !isChromelessPath && (hostApp === 'legacy' || hostApp === 'portal');

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleCollapse = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!showChrome || !isMobile || !sidebarOpen) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showChrome, isMobile, sidebarOpen]);

  if (loading) return null;

  if (!showChrome) {
    return <AppRoutes />;
  }

  const isSidebarCollapsedLayout = !isMobile && sidebarCollapsed;

  return (
    <>
      <div className="app-body">
        <Sidebar open={sidebarOpen} isMobile={isMobile} collapsed={sidebarCollapsed} onClose={closeSidebar} onToggle={toggleSidebar} onToggleCollapse={toggleCollapse} />
        <div className={`app-content ${isMobile ? 'app-content-mobile' : (isSidebarCollapsedLayout ? 'app-content-collapsed' : '')}`}>
          <TopBar isMobile={isMobile} onMenuToggle={toggleSidebar} />
          <main className={`app-main ${isMobile ? 'app-main-mobile' : ''}`}>
            <AppRoutes />
          </main>
        </div>
      </div>
    </>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <DeprecatedPortalHostRedirect />
      <AuthProvider>
        <BreadcrumbProvider>
          <TopBarActionsProvider>
            <AppContent />
          </TopBarActionsProvider>
        </BreadcrumbProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
